import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, UserRound } from "lucide-react";
import { getUserEntitlements } from "../config/plans";
import { normalizeEditorMainGame } from "../utils/editorSpotlight";

const MINT = "#10b981";
const PURPLE = "#a855f7";
const BG = "#0a0f0d";

export default function EditorSpotlightCard({
  editor,
  variant = "wide",
}) {
  const profile = editor?.profileInfo || {};
  const username = profile.username || editor?.id || "editor";
  const entitlements = getUserEntitlements(editor);
  const mainGame = normalizeEditorMainGame(profile.mainGame) || "Gaming";
  const isWide = variant === "wide";

  return (
    <Link
      to={`/editor/${username}`}
      className={`group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] transition hover:-translate-y-1 hover:border-white/20 ${
        isWide ? "p-5 sm:p-6" : "p-4"
      }`}
      style={{
        boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
      }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full opacity-30 blur-3xl"
        style={{
          background: `radial-gradient(circle, ${PURPLE} 0%, transparent 65%)`,
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-0 left-0 h-36 w-36 rounded-full opacity-25 blur-3xl"
        style={{
          background: `radial-gradient(circle, ${MINT} 0%, transparent 65%)`,
        }}
      />

      <div className="relative flex items-start gap-4">
        <div
          className={`overflow-hidden rounded-2xl border ${
            isWide ? "h-20 w-20" : "h-16 w-16"
          }`}
          style={{
            borderColor: `${MINT}55`,
            background: `linear-gradient(135deg, ${PURPLE}30, ${MINT}22)`,
          }}
        >
          {profile.profilePicUrl ? (
            <img
              src={profile.profilePicUrl}
              alt={username}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="grid h-full w-full place-items-center">
              <UserRound className="h-8 w-8 text-white/60" />
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider"
              style={{
                background: `${MINT}1e`,
                border: `1px solid ${MINT}44`,
                color: MINT,
              }}
            >
              <Sparkles className="h-3 w-3" />
              Spotlight
            </span>
            {entitlements.higherFeaturedRotationPriority && (
              <span className="inline-flex rounded-full border border-white/10 bg-white/[0.05] px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white/75">
                Highest Priority
              </span>
            )}
          </div>

          <h3 className="mt-3 truncate text-lg font-bold text-white sm:text-xl">
            @{username}
          </h3>
          <p className="mt-1 text-sm text-zinc-300">{mainGame} Editor</p>
          {profile.tagline && (
            <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-zinc-400">
              {profile.tagline}
            </p>
          )}
        </div>
      </div>

      <div className="relative mt-5 inline-flex items-center gap-2 text-sm font-semibold text-white">
        View profile
        <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
      </div>
    </Link>
  );
}

export function SpotlightEmptyState({ copy }) {
  return (
    <div
      className="rounded-3xl border border-dashed border-white/10 bg-white/[0.02] p-6 text-sm text-zinc-500"
      style={{ backgroundColor: BG }}
    >
      {copy}
    </div>
  );
}
